import { useState, useEffect, ReactNode } from "react";
import {
    getMaintenanceStatus,
    verifyMaintenanceCode,
    MaintenanceStatus
} from "../../lib/api/maintenance";
import MaintenanceGatePage from "./MaintenanceGatePage";

interface MaintenanceGuardProps {
    children: ReactNode;
}

type GuardState = 'loading' | 'blocked' | 'allowed';

export const MaintenanceGuard = ({ children }: MaintenanceGuardProps) => {
    const [state, setState] = useState<GuardState>('loading');
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        checkMaintenanceStatus();
    }, []);

    const checkMaintenanceStatus = async () => {
        try {
            const status: MaintenanceStatus = await getMaintenanceStatus();

            if (!status.maintenanceMode) {
                setState('allowed');
            } else if (status.hasAccess) {
                setState('allowed');
            } else {
                setState('blocked');
            }
        } catch (err) {
            console.warn('Failed to check maintenance status, allowing access:', err);
            setState('allowed');
        }
    };

    const handleVerify = async (code: string) => {
        setError(undefined);
        try {
            await verifyMaintenanceCode(code);
            setState('allowed');
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.message || 'Invalid access code';
            setError(errorMessage);
            throw err;
        }
    };

    const handleAccessGranted = () => {
        setState('allowed');
    };

    if (state === 'loading') {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                    <p className="text-slate-400 text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    if (state === 'blocked') {
        return (
            <MaintenanceGatePage
                onAccessGranted={handleAccessGranted}
                onVerify={handleVerify}
                error={error}
            />
        );
    }

    return <>{children}</>;
};

export default MaintenanceGuard;
